import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, PageHeader } from '@/components/ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '@/components/DataState';
import { SortableTable, type SortableColumn } from '@/components/ui/SortableTable';
import { fetchSeoProjectOptions } from '@/data/sources';
import { fetchSeoDomainComparison } from '@/data/sources-seob';
import type { DomainComparisonResult, DomainGapRow } from '@/types/seob';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useActiveDomain } from '@/hooks/useActiveDomain';
import { resolveActiveProjectId } from '@/pages/seo/domain-scope';
import { DomainChoiceGate } from '@/pages/seo/DomainChoiceGate';

/* /seo/domain-comparison — Domain Comparison / content gaps (W2a). Read-only,
 * source-attributed content-gap keywords vs one competitor domain, from
 * DataForSEO Labs domain_intersection.
 *
 * HONESTY + SAFETY INVARIANTS (W2a contract):
 *  - PAID (DataForSEO Labs content gaps): NEVER auto-fires. Project options is a
 *    free list read; the comparison runs ONLY on an explicit click. No useQuery
 *    is wired to the paid read, so mounting this page charges nothing.
 *  - Source-attributed (source + provider fetched_at + cache indicator); every
 *    null position renders as an em-dash — a missing rank is NEVER invented.
 *  - Results render in the shared SortableTable (client-side sort only — free).
 *  - Honest states: idle / loading / error / not-configured (503).
 */

function sourceLabel(source: string): string {
  if (source.startsWith('dataforseo.labs')) return 'DataForSEO Labs';
  if (source.startsWith('dataforseo')) return 'DataForSEO';
  return source;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-GB');
}

/** em-dash for any null provider metric — never a fabricated value. */
function orInt(v: number | null): string {
  return v === null ? '—' : formatInt(v);
}

/** Rank order for the opportunity label so the column sorts high → low sensibly. */
function opportunityRank(v: string): number {
  const s = v.toLowerCase();
  if (s === 'high') return 3;
  if (s === 'medium' || s === 'med') return 2;
  if (s === 'low') return 1;
  return 0; // unknown / em-dash sorts below the known bands
}

function opportunityTone(v: string): 'success' | 'warning' | 'neutral' {
  const s = v.toLowerCase();
  if (s === 'high') return 'success';
  if (s === 'medium' || s === 'med') return 'warning';
  return 'neutral';
}

interface Panel {
  title: string;
  body: string;
}

type SectionState<T> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; panel: Panel }
  | { status: 'done'; data: T };

function describeError(err: unknown): Panel {
  if (err instanceof ApiError) {
    if (err.status === 503 || err.code === 'provider_unavailable') {
      return {
        title: 'Domain comparison isn’t connected yet',
        body: 'Live content-gap data needs the DataForSEO Labs connection, which isn’t configured for this environment yet. Nothing was fetched and nothing was charged — an honest “not configured” state, never placeholder data.',
      };
    }
    if (err.status === 402 || err.code === 'budget_exceeded') {
      return {
        title: 'Monthly provider budget reached',
        body: 'This lookup would exceed your workspace’s monthly provider budget, so it was refused before any provider call was made — nothing was spent. It resumes when the budget resets, or after you raise the cap on Spend & Budget.',
      };
    }
    if (err.status === 429 || err.code === 'rate_limited') {
      return {
        title: 'Provider rate limit reached',
        body: 'This workspace has hit the provider rate limit, so nothing was fetched or charged. Try again as the window rolls over.',
      };
    }
    if (err.status === 502 || err.code === 'provider_error') {
      return {
        title: 'The provider request failed',
        body: `${err.message ? `${err.message} ` : ''}Nothing was stored or charged — try again in a moment.`,
      };
    }
    if (err.status === 404) {
      return {
        title: 'Project not found',
        body: 'This SEO project isn’t available to your workspace. Pick another project and try again.',
      };
    }
    return {
      title: 'The lookup could not be completed',
      body: err.message || 'The data service returned an unexpected response. Nothing was charged — try again.',
    };
  }
  return {
    title: 'The lookup could not be completed',
    body: err instanceof Error ? err.message : 'Unexpected error — nothing was charged. Try again.',
  };
}

function SourceFooter({
  source,
  fetchedAt,
  cacheHit,
}: {
  source: string;
  fetchedAt: string | null;
  cacheHit: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border-subtle px-3 py-2 text-[11px] text-text-tertiary">
      <Badge tone="neutral">Source: {sourceLabel(source)}</Badge>
      <Badge tone={cacheHit ? 'neutral' : 'success'}>
        {cacheHit ? 'Served from cache' : 'Live result'}
      </Badge>
      <span>{fetchedAt ? `Last updated ${fetchedAt}` : 'Update time not reported'}</span>
    </div>
  );
}

const NUM_CELL = 'font-mono text-text-secondary';

const GAP_COLUMNS: SortableColumn<DomainGapRow>[] = [
  {
    key: 'keyword',
    header: 'Keyword',
    sortable: true,
    sortValue: (g) => g.keyword,
    render: (g) => g.keyword,
    truncate: true,
    maxWidthClass: 'max-w-[280px]',
    title: (g) => g.keyword,
    cellClassName: 'font-medium text-text-primary',
  },
  {
    key: 'your_position',
    header: 'Your position',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (g) => g.your_position,
    render: (g) => orInt(g.your_position),
    cellClassName: `${NUM_CELL} text-text-primary`,
  },
  {
    key: 'their_position',
    header: 'Their position',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (g) => g.their_position,
    render: (g) => orInt(g.their_position),
    cellClassName: NUM_CELL,
  },
  {
    key: 'volume',
    header: 'Volume',
    align: 'right',
    numeric: true,
    sortable: true,
    sortValue: (g) => g.volume,
    render: (g) => orInt(g.volume),
    cellClassName: NUM_CELL,
  },
  {
    key: 'opportunity',
    header: 'Opportunity',
    numeric: true,
    sortable: true,
    sortValue: (g) => opportunityRank(g.opportunity),
    render: (g) =>
      g.opportunity === '—' ? (
        <span className="text-text-tertiary">—</span>
      ) : (
        <Badge tone={opportunityTone(g.opportunity)}>{g.opportunity}</Badge>
      ),
    cellClassName: 'text-text-secondary',
  },
];

export function SeoDomainComparisonPage() {
  const { activeWorkspace } = useAuth();
  const canManage = activeWorkspace?.role === 'owner' || activeWorkspace?.role === 'operator';
  const navigate = useNavigate();

  const projectsQ = useQuery({
    queryKey: ['seo', 'project-options'],
    queryFn: fetchSeoProjectOptions,
  });
  const projects = projectsQ.data ?? [];
  const { active, select } = useActiveDomain();

  const projectId = resolveActiveProjectId(active, projects);

  const [competitor, setCompetitor] = useState('');
  const [comparison, setComparison] = useState<SectionState<DomainComparisonResult>>({
    status: 'idle',
  });

  const runComparison = async () => {
    if (projectId === null || comparison.status === 'loading') return;
    const trimmed = competitor.trim();
    if (trimmed === '') return;
    setComparison({ status: 'loading' });
    try {
      const data = await fetchSeoDomainComparison(projectId, trimmed);
      setComparison({ status: 'done', data });
    } catch (err) {
      setComparison({ status: 'error', panel: describeError(err) });
    }
  };

  const onProjectChange = (id: string) => {
    void select(Number(id));
    setComparison({ status: 'idle' });
  };

  if (projectsQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Domain Comparison" />
        <LoadingState rows={4} />
      </div>
    );
  }
  if (projectsQ.error) {
    return (
      <div className="space-y-6">
        <PageHeader title="Domain Comparison" />
        <ErrorState error={projectsQ.error} onRetry={() => void projectsQ.refetch()} />
      </div>
    );
  }
  if (projects.length > 0 && projectId === null) {
    return <DomainChoiceGate options={projects} onSelect={(id) => void select(Number(id))} />;
  }
  if (projects.length === 0) {
    return (
      <div className="space-y-6">
        <PageHeader title="Domain Comparison" />
        <EmptyState
          title="No project to explore yet"
          teach="Domain Comparison runs against an SEO project. Add your first domain on the Projects page, then come back here to find the keywords a competitor ranks for that you don't."
          action={
            <Button variant="primary" onClick={() => navigate('/seo/projects')}>
              Go to Projects
            </Button>
          }
        />
      </div>
    );
  }

  const inputClass =
    'h-control rounded-md border border-border-default bg-canvas px-3 text-sm text-text-primary outline-none focus:border-ai focus:ring-2 focus:ring-ai/30';

  const projectSelector = (
    <label className="flex items-center gap-2">
      <span className="text-xs font-medium text-text-secondary">Domain</span>
      <select
        value={projectId ?? ''}
        onChange={(e) => onProjectChange(e.target.value)}
        className={inputClass}
        aria-label="SEO project"
      >
        {projects.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Domain Comparison"
        subtitle="The keywords a competitor ranks for that you don't yet — content gaps from licensed DataForSEO Labs data, loaded on demand, source-attributed, and honest about every missing metric."
        actions={projectSelector}
      />

      {!canManage && (
        <div role="status" className="rounded-md border border-border-subtle bg-inset/50 px-3 py-2 text-sm text-text-secondary">
          Domain comparison uses paid provider data, so it’s limited to workspace owners and
          operators.
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="engine">DataForSEO Labs</Badge>
          <span className="text-sm font-medium text-text-primary">Content gaps</span>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-text-tertiary">Paid · budget-guarded · 7-day cache</span>
          </div>
        </div>
        <p className="mt-1 text-sm text-text-secondary">
          Enter a competitor domain to see keywords they rank for and you don’t, with both
          positions, volume and an opportunity band. Runs only when you press the button — nothing
          is fetched or charged on load.
        </p>

        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void runComparison();
          }}
        >
          <input
            type="text"
            value={competitor}
            onChange={(e) => setCompetitor(e.target.value)}
            placeholder="e.g. competitor.com"
            aria-label="Competitor domain"
            className={`${inputClass} min-w-[16rem] flex-1`}
            disabled={!canManage}
          />
          <Button
            type="submit"
            variant="primary"
            disabled={!canManage || comparison.status === 'loading' || competitor.trim() === ''}
            title={canManage ? undefined : 'Owner/operator only — this uses paid provider data'}
          >
            {comparison.status === 'loading'
              ? 'Loading…'
              : comparison.status === 'done'
                ? 'Refresh'
                : 'Compare domains'}
          </Button>
        </form>

        <div className="mt-4">
          {comparison.status === 'idle' && (
            <p className="text-sm text-text-tertiary">
              No data yet — enter a competitor domain and run the comparison.
            </p>
          )}
          {comparison.status === 'loading' && <LoadingState rows={4} />}
          {comparison.status === 'error' && (
            <div role="status" className="rounded-md border border-border-default bg-surface px-4 py-3">
              <div className="text-sm font-medium text-text-primary">{comparison.panel.title}</div>
              <p className="mt-1 text-sm text-text-secondary">{comparison.panel.body}</p>
            </div>
          )}
          {comparison.status === 'done' &&
            (comparison.data.gaps.length === 0 ? (
              <div className="rounded-md border border-border-subtle bg-canvas p-3">
                <p className="text-sm text-text-secondary">
                  The provider returned no content gaps against this competitor — an honest empty
                  result, not an error.
                </p>
                <div className="mt-2">
                  <SourceFooter
                    source={comparison.data.source}
                    fetchedAt={comparison.data.fetched_at}
                    cacheHit={comparison.data.cache_hit}
                  />
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-subtle bg-surface">
                <SortableTable
                  rows={comparison.data.gaps}
                  columns={GAP_COLUMNS}
                  getRowKey={(g, i) => `${g.keyword}-${i}`}
                  minWidthClass="min-w-[680px]"
                  caption="Content-gap keywords with your position, their position, volume and opportunity"
                />
                <SourceFooter
                  source={comparison.data.source}
                  fetchedAt={comparison.data.fetched_at}
                  cacheHit={comparison.data.cache_hit}
                />
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}
